import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { Label } from '@os/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Textarea } from '@os/components/ui/textarea';
import { Badge } from '@os/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@os/components/ui/table';
import { TestTube, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
// @ts-ignore
import jStat from 'jstat';
import * as ss from 'simple-statistics';

interface HypothesisTestingProps {
  data: number[];
}

interface TestResult {
  testType: string;
  testStatistic: number;
  pValue: number;
  criticalValue?: number;
  degreesOfFreedom?: number;
  conclusion: string;
  interpretation: string;
  assumptions: string[];
}

export const HypothesisTesting: React.FC<HypothesisTestingProps> = ({ data }) => {
  const [testType, setTestType] = useState('one-sample-t');
  const [hypothesizedMean, setHypothesizedMean] = useState('0');
  const [secondDataset, setSecondDataset] = useState('');
  const [significanceLevel, setSignificanceLevel] = useState('0.05');
  const [alternativeHypothesis, setAlternativeHypothesis] = useState('two-tailed');
  const [result, setResult] = useState<TestResult | null>(null);

  const parseSecondDataset = (input: string): number[] => {
    try {
      const cleaned = input.replace(/[^\d\.\-\s,\n]/g, '');
      return cleaned
        .split(/[\s,\n]+/)
        .filter(v => v.trim() !== '')
        .map(v => parseFloat(v.trim()))
        .filter(v => !isNaN(v));
    } catch {
      return [];
    }
  };

  const performOneSampleTTest = () => {
    if (data.length === 0) return null;

    const mu0 = parseFloat(hypothesizedMean);
    const alpha = parseFloat(significanceLevel);
    const n = data.length;
    const sampleMean = ss.mean(data);
    const sampleStd = ss.standardDeviation(data);
    const standardError = sampleStd / Math.sqrt(n);
    
    // Calculate t-statistic
    const tStatistic = (sampleMean - mu0) / standardError;
    const df = n - 1;
    
    // Calculate p-value based on alternative hypothesis
    let pValue: number;
    let criticalValue: number;
    
    if (alternativeHypothesis === 'two-tailed') {
      pValue = 2 * (1 - jStat.studentt.cdf(Math.abs(tStatistic), df));
      criticalValue = jStat.studentt.inv(1 - alpha/2, df);
    } else if (alternativeHypothesis === 'greater') {
      pValue = 1 - jStat.studentt.cdf(tStatistic, df);
      criticalValue = jStat.studentt.inv(1 - alpha, df);
    } else {
      pValue = jStat.studentt.cdf(tStatistic, df);
      criticalValue = jStat.studentt.inv(alpha, df);
    }

    const isSignificant = pValue < alpha;
    const conclusion = isSignificant ? 'Reject H₀' : 'Fail to reject H₀';
    
    let interpretation = '';
    if (alternativeHypothesis === 'two-tailed') {
      interpretation = isSignificant 
        ? `The sample mean (${sampleMean.toFixed(4)}) is significantly different from the hypothesized mean (${mu0}).`
        : `There is insufficient evidence that the sample mean differs from the hypothesized mean (${mu0}).`;
    } else if (alternativeHypothesis === 'greater') {
      interpretation = isSignificant
        ? `The sample mean (${sampleMean.toFixed(4)}) is significantly greater than the hypothesized mean (${mu0}).`
        : `There is insufficient evidence that the sample mean is greater than the hypothesized mean (${mu0}).`;
    } else {
      interpretation = isSignificant
        ? `The sample mean (${sampleMean.toFixed(4)}) is significantly less than the hypothesized mean (${mu0}).`
        : `There is insufficient evidence that the sample mean is less than the hypothesized mean (${mu0}).`;
    }

    return {
      testType: 'One-Sample t-Test',
      testStatistic: tStatistic,
      pValue,
      criticalValue,
      degreesOfFreedom: df,
      conclusion,
      interpretation,
      assumptions: [
        'Data is normally distributed or sample size is large (n ≥ 30)',
        'Observations are independent',
        'Data is measured at interval or ratio level'
      ]
    };
  };

  const performTwoSampleTTest = () => {
    const secondData = parseSecondDataset(secondDataset);
    if (data.length === 0 || secondData.length === 0) return null;

    const alpha = parseFloat(significanceLevel);
    const n1 = data.length;
    const n2 = secondData.length;
    const mean1 = ss.mean(data);
    const mean2 = ss.mean(secondData);
    const var1 = ss.variance(data);
    const var2 = ss.variance(secondData);
    
    // Pooled variance for equal variances assumption
    const pooledVar = ((n1 - 1) * var1 + (n2 - 1) * var2) / (n1 + n2 - 2);
    const standardError = Math.sqrt(pooledVar * (1/n1 + 1/n2));
    
    const tStatistic = (mean1 - mean2) / standardError;
    const df = n1 + n2 - 2;
    
    let pValue: number;
    let criticalValue: number;
    
    if (alternativeHypothesis === 'two-tailed') {
      pValue = 2 * (1 - jStat.studentt.cdf(Math.abs(tStatistic), df));
      criticalValue = jStat.studentt.inv(1 - alpha/2, df);
    } else if (alternativeHypothesis === 'greater') {
      pValue = 1 - jStat.studentt.cdf(tStatistic, df);
      criticalValue = jStat.studentt.inv(1 - alpha, df);
    } else {
      pValue = jStat.studentt.cdf(tStatistic, df);
      criticalValue = jStat.studentt.inv(alpha, df);
    }

    const isSignificant = pValue < alpha;
    const conclusion = isSignificant ? 'Reject H₀' : 'Fail to reject H₀';
    
    let interpretation = '';
    if (alternativeHypothesis === 'two-tailed') {
      interpretation = isSignificant 
        ? `The means of the two groups are significantly different (Group 1: ${mean1.toFixed(4)}, Group 2: ${mean2.toFixed(4)}).`
        : `There is insufficient evidence that the means of the two groups differ.`;
    } else if (alternativeHypothesis === 'greater') {
      interpretation = isSignificant
        ? `Group 1 mean (${mean1.toFixed(4)}) is significantly greater than Group 2 mean (${mean2.toFixed(4)}).`
        : `There is insufficient evidence that Group 1 mean is greater than Group 2 mean.`;
    } else {
      interpretation = isSignificant
        ? `Group 1 mean (${mean1.toFixed(4)}) is significantly less than Group 2 mean (${mean2.toFixed(4)}).`
        : `There is insufficient evidence that Group 1 mean is less than Group 2 mean.`;
    }

    return {
      testType: 'Two-Sample t-Test (Equal Variances)',
      testStatistic: tStatistic,
      pValue,
      criticalValue,
      degreesOfFreedom: df,
      conclusion,
      interpretation,
      assumptions: [
        'Both groups are normally distributed or sample sizes are large',
        'Observations are independent within and between groups',
        'Groups have equal variances (homoscedasticity)',
        'Data is measured at interval or ratio level'
      ]
    };
  };

  const performOneSampleZTest = () => {
    if (data.length === 0) return null;

    const mu0 = parseFloat(hypothesizedMean);
    const alpha = parseFloat(significanceLevel);
    const n = data.length;
    const sampleMean = ss.mean(data);
    const sampleStd = ss.standardDeviation(data);
    const standardError = sampleStd / Math.sqrt(n);
    
    // Z-statistic
    const zStatistic = (sampleMean - mu0) / standardError;
    
    // Calculate p-value and critical value
    let pValue: number;
    let criticalValue: number;
    
    if (alternativeHypothesis === 'two-tailed') {
      pValue = 2 * (1 - jStat.normal.cdf(Math.abs(zStatistic), 0, 1));
      criticalValue = jStat.normal.inv(1 - alpha/2, 0, 1);
    } else if (alternativeHypothesis === 'greater') {
      pValue = 1 - jStat.normal.cdf(zStatistic, 0, 1);
      criticalValue = jStat.normal.inv(1 - alpha, 0, 1);
    } else {
      pValue = jStat.normal.cdf(zStatistic, 0, 1);
      criticalValue = jStat.normal.inv(alpha, 0, 1);
    }

    const isSignificant = pValue < alpha;
    const conclusion = isSignificant ? 'Reject H₀' : 'Fail to reject H₀';
    
    let interpretation = '';
    if (alternativeHypothesis === 'two-tailed') {
      interpretation = isSignificant 
        ? `The sample mean (${sampleMean.toFixed(4)}) is significantly different from the hypothesized mean (${mu0}).`
        : `There is insufficient evidence that the sample mean differs from the hypothesized mean (${mu0}).`;
    }

    return {
      testType: 'One-Sample Z-Test',
      testStatistic: zStatistic,
      pValue,
      criticalValue,
      degreesOfFreedom: undefined,
      conclusion,
      interpretation,
      assumptions: [
        'Population standard deviation is known (or sample size is very large)',
        'Data is normally distributed',
        'Observations are independent'
      ]
    };
  };

  const runTest = () => {
    let testResult: TestResult | null = null;

    switch (testType) {
      case 'one-sample-t':
        testResult = performOneSampleTTest();
        break;
      case 'two-sample-t':
        testResult = performTwoSampleTTest();
        break;
      case 'one-sample-z':
        testResult = performOneSampleZTest();
        break;
    }

    setResult(testResult);
  };

  const testOptions = [
    { value: 'one-sample-t', label: 'One-Sample t-Test', description: 'Test if sample mean differs from hypothesized value' },
    { value: 'two-sample-t', label: 'Two-Sample t-Test', description: 'Compare means of two independent groups' },
    { value: 'one-sample-z', label: 'One-Sample Z-Test', description: 'Test if sample mean differs (known population SD)' }
  ];

  const alternativeOptions = [
    { value: 'two-tailed', label: 'Two-tailed (≠)', description: 'Mean is different from hypothesized value' },
    { value: 'greater', label: 'Greater than (>)', description: 'Mean is greater than hypothesized value' },
    { value: 'less', label: 'Less than (<)', description: 'Mean is less than hypothesized value' }
  ];

  if (data.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <TestTube className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-muted-foreground">Enter numerical data in the sidebar to begin hypothesis testing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-500 text-white rounded-lg">
          <TestTube size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Hypothesis Testing</h2>
          <p className="text-muted-foreground">Statistical significance testing and inference</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Test Configuration */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Test Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="test-type">Statistical Test</Label>
                <Select value={testType} onValueChange={setTestType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {testOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(testType === 'one-sample-t' || testType === 'one-sample-z') && (
                <div>
                  <Label htmlFor="hypothesized-mean">Hypothesized Mean (μ₀)</Label>
                  <Input
                    id="hypothesized-mean"
                    type="number"
                    step="any"
                    value={hypothesizedMean}
                    onChange={(e) => setHypothesizedMean(e.target.value)}
                    placeholder="0"
                  />
                </div>
              )}

              {testType === 'two-sample-t' && (
                <div>
                  <Label htmlFor="second-dataset">Second Dataset</Label>
                  <Textarea
                    id="second-dataset"
                    placeholder="Enter second group data..."
                    value={secondDataset}
                    onChange={(e) => setSecondDataset(e.target.value)}
                    className="min-h-[80px]"
                  />
                  {parseSecondDataset(secondDataset).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Parsed: {parseSecondDataset(secondDataset).length} values
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="alternative">Alternative Hypothesis</Label>
                <Select value={alternativeHypothesis} onValueChange={setAlternativeHypothesis}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {alternativeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="significance-level">Significance Level (α)</Label>
                <Select value={significanceLevel} onValueChange={setSignificanceLevel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.01">0.01 (99% confidence)</SelectItem>
                    <SelectItem value="0.05">0.05 (95% confidence)</SelectItem>
                    <SelectItem value="0.10">0.10 (90% confidence)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={runTest} className="w-full">
                Run Hypothesis Test
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          {result && (
            <div className="space-y-4">
              {/* Test Results Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {result.conclusion === 'Reject H₀' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    {result.testType} Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Test Statistic</div>
                      <div className="text-lg font-mono font-bold">{result.testStatistic.toFixed(4)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">p-value</div>
                      <div className="text-lg font-mono font-bold">
                        {result.pValue < 0.001 ? '<0.001' : result.pValue.toFixed(4)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Critical Value</div>
                      <div className="text-lg font-mono font-bold">
                        {result.criticalValue ? `±${Math.abs(result.criticalValue).toFixed(4)}` : 'N/A'}
                      </div>
                    </div>
                    {result.degreesOfFreedom && (
                      <div>
                        <div className="text-sm text-muted-foreground">Degrees of Freedom</div>
                        <div className="text-lg font-mono font-bold">{result.degreesOfFreedom}</div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={result.conclusion === 'Reject H₀' ? 'default' : 'secondary'}>
                        {result.conclusion}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        at α = {significanceLevel}
                      </span>
                    </div>

                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm font-medium mb-1">Interpretation:</div>
                      <div className="text-sm">{result.interpretation}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assumptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    Test Assumptions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground mb-3">
                    Please verify that your data meets these assumptions:
                  </div>
                  <ul className="space-y-2">
                    {result.assumptions.map((assumption, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                        {assumption}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}

          {!result && (
            <Card>
              <CardContent className="py-12 text-center">
                <TestTube className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Configure Your Test</h3>
                <p className="text-muted-foreground">
                  Select a statistical test and configure the parameters to begin hypothesis testing.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};